import React, { useState, useContext } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

// My Files
import firebase from "../../firebase";
import { UserContext } from "../../context";

export default function ChangeEmailDialog(props) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newEmail, setNewEmail] = useState("");

    const [inProgress, setInProgress] = useState(false);

    const [statusMessage, setStatusMessage] = useState("");

    const { currentUserData } = useContext(UserContext);

    const handleClose = () => {
        if (inProgress) {
            return;
        }

        setCurrentPassword("");
        setNewEmail("");
        setStatusMessage("");

        props.onClose();
    };

    const handleChange = (event) => {
        setStatusMessage("");

        if (event.target.id === "currentpassword") {
            setCurrentPassword(event.target.value);
        } else if (event.target.id === "email") {
            setNewEmail(event.target.value);
        }
    };

    function handleOKPress(event) {
        event.preventDefault();

        if (!currentPassword || !newEmail || inProgress) {
            return;
        }

        const auth = firebase.auth();
        const user = auth.currentUser;

        setInProgress(true);
        setStatusMessage("");

        if (user) {
            const credential = firebase.auth.EmailAuthProvider.credential(
                user.email,
                currentPassword,
            );

            user.reauthenticateWithCredential(credential)
                .then(() => {
                    user.updateEmail(newEmail)
                        .then(() => {
                            setInProgress(false);

                            // Has been updated in auth, now change in the
                            // database. The address lives only on
                            // /userprofiles.
                            firebase
                                .database()
                                .ref("userprofiles")
                                .child(currentUserData.key)
                                .update({ email: newEmail })
                                .then(() => {
                                    props.onSuccess();

                                    setCurrentPassword("");
                                    setNewEmail("");
                                    setStatusMessage("");
                                })
                                .catch((error) => {
                                    // Sign-in already uses the new address;
                                    // only the copy shown on the site and
                                    // used for notifications is stale.
                                    console.error(
                                        "Unable to save the new email",
                                        error,
                                    );
                                    setStatusMessage(
                                        "Your sign-in email has changed, but " +
                                            "it could not be saved to your " +
                                            "profile. Please contact support.",
                                    );
                                });
                        })
                        .catch((error) => {
                            setInProgress(false);
                            setStatusMessage(error.message);
                        });
                })
                .catch(() => {
                    setCurrentPassword("");

                    setStatusMessage("Password is incorrect");
                    setInProgress(false);
                });
        } else {
            setInProgress(false);
            setStatusMessage("Your session has expired. Please sign in again.");
        }
    }

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="change-email-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <EmailOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="change-email-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Update email
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, lineHeight: 1.55 }}
                    >
                        Change the email address you use to sign in.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close update email"
                    onClick={handleClose}
                    disabled={inProgress}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>

                {inProgress && (
                    <LinearProgress
                        aria-label="Updating email"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    />
                )}
            </Box>

            <Box component="form" noValidate onSubmit={handleOKPress}>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {statusMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {statusMessage}
                        </Alert>
                    )}

                    <Box
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1.5, fontWeight: 800 }}
                        >
                            Verify your identity
                        </Typography>
                        <Box sx={{ display: "grid", gap: 2 }}>
                            <TextField
                                id="currentpassword"
                                label="Current password"
                                type="password"
                                autoComplete="current-password"
                                variant="outlined"
                                value={currentPassword}
                                onChange={handleChange}
                                autoFocus
                                required
                                fullWidth
                                disabled={inProgress}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <TextField
                                id="email"
                                label="New email address"
                                type="email"
                                autoComplete="email"
                                variant="outlined"
                                value={newEmail}
                                onChange={handleChange}
                                required
                                fullWidth
                                disabled={inProgress}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1.25 }}
                        >
                            Your password is required before your sign-in email
                            can be changed.
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}
                >
                    <Button
                        color="inherit"
                        onClick={handleClose}
                        disabled={inProgress}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={!currentPassword || !newEmail || inProgress}
                    >
                        {inProgress ? "Updating…" : "Update email"}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
